import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import FormContainer from 'components/LoanForm/FormContainer';
import ToggleButton, { ToggleButtonType } from 'components/ToggleButton';
import Button from 'components/Button';
import Input from 'components/Input';
import NumberInput from 'components/NumberInput';

import { FlowComponentType } from 'routes/types';
import { setCustomGoals } from 'handlers/yourGoal';
import { AICoachGoalPriority, AICoachGoalType } from 'enums/AICoach';
import { getLoanGoal } from 'selectors/getLoanGoal';
import { getApplicationData } from 'selectors/getApplicationData';
import { AICoachGoalItem } from 'api/LoanOfferApi';
import { updateAICoachGoals } from 'thunks';
import { RoutePath } from 'enums/Routes';
import { useNavigate } from 'hooks/useNavigate';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { toggleAIChat } from 'handlers/aiChat';

import { getMessageForRequiredFields, getMessageForInvalidFields } from 'utils/errors';
import { getParsedNumericValue } from 'utils/getParsedNumericValue';
import { UpdateGoalsMode } from 'api/AICoachGoalsApi';
import { resetGoalOptimizerState } from 'handlers/goalOptimizer';
import { resetAiCoachConversation } from 'handlers/aiCoachConversation';
import { isEqual } from 'utils/objects';
import { hasPrimaryGoalCompleted, selectedGoalRequiresMonthlySavings } from 'utils/aiCoachUtils';

import styles from './Goals.module.scss';

export const aiCoachGoalsList = Object.freeze([
  { title: 'Pay down credit card debt', value: AICoachGoalType.PayDownCreditCardDebt },
  { title: 'Pay down student loan debt', value: AICoachGoalType.PayDownStudentLoanDebt },
  { title: 'Build an emergency fund', value: AICoachGoalType.BuildEmergencyFund },
  { title: 'Save for a specific expense', value: AICoachGoalType.SaveForSpecificExpense },
  { title: 'Improve my credit score', value: AICoachGoalType.ImproveCreditScore },
  { title: 'Save for retirement', value: AICoachGoalType.SaveForRetirement },
  { title: 'Other', value: AICoachGoalType.Other },
]);

export enum YourGoalVariant {
  Default,
  CardCrossSell,
}

export interface GoalsState {
  isFromFinancialDashboard?: boolean;
  goalsChanged?: boolean;
}

export type AICoachGoalsFlowState = GoalsState;

enum FormFields {
  OtherGoal = 'otherGoal',
  OtherGoalAmount = 'otherGoalAmount',
  ExpenseGoal = 'expenseGoal',
  ExpenseAmount = 'expenseAmount',
}

const FormFieldValidationLabels = Object.freeze({
  [FormFields.OtherGoal]: 'Goal',
  [FormFields.OtherGoalAmount]: 'Goal Amount',
  [FormFields.ExpenseGoal]: 'Expense',
  [FormFields.ExpenseAmount]: 'Expense Amount',
});

// Alphanumeric but not purely numeric
const ALPHANUMERIC_NOT_NUMERIC_PATTERN = /^(?=.*[a-zA-Z])[a-zA-Z0-9\s]{3,}$/;

const normalizeGoals = (goals: AICoachGoalItem[]) =>
  goals.map(({ type, targetValue, description, monthlySavingsAmount }) => ({
    type,
    targetValue,
    description,
    monthlySavingsAmount,
  }));

const AICoachGoals = ({ handleNext }: FlowComponentType) => {
  const { aiCoachGoals, isLoading } = useSelector(getLoanGoal);
  const { application } = useSelector(getApplicationData);

  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const navigate = useNavigate();
  const location = useLocation();
  const isFromFinancialDashboard = (location.state as GoalsState)?.isFromFinancialDashboard;

  const effectiveGoals = hasPrimaryGoalCompleted(aiCoachGoals) ? [] : aiCoachGoals;

  const initialGoals = useRef(normalizeGoals(effectiveGoals ?? []));

  const otherGoalItem = effectiveGoals?.find((item) => item.type === AICoachGoalType.Other);
  const expenseGoalItem = effectiveGoals?.find((item) => item.type === AICoachGoalType.SaveForSpecificExpense);

  const otherGoal = otherGoalItem?.description;
  const otherGoalAmount = otherGoalItem?.targetValue?.toString();
  const expenseGoal = expenseGoalItem?.description;
  const expenseAmount = expenseGoalItem?.targetValue?.toString();

  const isOtherSelected = effectiveGoals?.some((item) => item.type === AICoachGoalType.Other);
  const isSaveForExpenseSelected = effectiveGoals?.some((item) => item.type === AICoachGoalType.SaveForSpecificExpense);

  const prevIsOtherSelected = useRef(isOtherSelected);
  const prevIsSaveForExpenseSelected = useRef(isSaveForExpenseSelected);

  useEffect(() => {
    // Only scroll when a value changes from false to true
    const otherJustSelected = !prevIsOtherSelected.current && isOtherSelected;
    const expenseJustSelected = !prevIsSaveForExpenseSelected.current && isSaveForExpenseSelected;

    if (otherJustSelected || expenseJustSelected) {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth',
      });
    }

    prevIsOtherSelected.current = isOtherSelected;
    prevIsSaveForExpenseSelected.current = isSaveForExpenseSelected;
  }, [isOtherSelected, isSaveForExpenseSelected]);

  const {
    formState: { errors, isValid },
    register,
    unregister,
    watch,
    setValue,
    trigger,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      otherGoal,
      otherGoalAmount: otherGoalAmount ? getParsedNumericValue(otherGoalAmount) : undefined,
      expenseGoal,
      expenseAmount: expenseAmount ? getParsedNumericValue(expenseAmount) : undefined,
    },
  });

  const watcher = watch();

  useEffect(() => {
    if (isOtherSelected) {
      register(FormFields.OtherGoal, {
        required: getMessageForRequiredFields(FormFieldValidationLabels[FormFields.OtherGoal]),
        pattern: {
          value: ALPHANUMERIC_NOT_NUMERIC_PATTERN,
          message: getMessageForInvalidFields(FormFieldValidationLabels[FormFields.OtherGoal]),
        },
      });
      register(FormFields.OtherGoalAmount, {
        required: getMessageForRequiredFields(FormFieldValidationLabels[FormFields.OtherGoalAmount]),
        min: {
          message: getMessageForInvalidFields(FormFieldValidationLabels[FormFields.OtherGoalAmount]),
          value: 2,
        },
      });
    } else {
      unregister(FormFields.OtherGoal);
      unregister(FormFields.OtherGoalAmount);
    }

    if (isSaveForExpenseSelected) {
      register(FormFields.ExpenseGoal, {
        required: getMessageForRequiredFields(FormFieldValidationLabels[FormFields.ExpenseGoal]),
        pattern: {
          value: ALPHANUMERIC_NOT_NUMERIC_PATTERN,
          message: getMessageForInvalidFields(FormFieldValidationLabels[FormFields.ExpenseGoal]),
        },
      });
      register(FormFields.ExpenseAmount, {
        required: getMessageForRequiredFields(FormFieldValidationLabels[FormFields.ExpenseAmount]),
        min: {
          message: getMessageForInvalidFields(FormFieldValidationLabels[FormFields.ExpenseAmount]),
          value: 2,
        },
      });
    } else {
      unregister(FormFields.ExpenseGoal);
      unregister(FormFields.ExpenseAmount);
    }
  }, [isOtherSelected, isSaveForExpenseSelected]);

  const handleGoalChange = (selectedValue: AICoachGoalType) => {
    dispatch(setCustomGoals([{ type: selectedValue }]));
  };

  const handleContinue = async () => {
    const customGoals: AICoachGoalItem[] =
      effectiveGoals?.map((goal) => {
        const item: AICoachGoalItem = {
          ...goal,
          priority: AICoachGoalPriority.Primary,
        };

        if (goal.type === AICoachGoalType.Other) {
          item.description = watcher.otherGoal?.trim();
          item.targetValue = watcher.otherGoalAmount;
        }

        if (goal.type === AICoachGoalType.SaveForSpecificExpense) {
          item.description = watcher.expenseGoal?.trim();
          item.targetValue = watcher.expenseAmount;
        }

        return item;
      }) ?? [];

    const selectedGoalTitles = customGoals.map(
      (goal) => goal.description ?? aiCoachGoalsList.find((g) => g.value === goal.type)?.title ?? goal.type,
    );
    analytics.track('User Selected Goal', { goals: selectedGoalTitles });

    dispatch(setCustomGoals(customGoals));

    const goalsChanged = !isEqual(initialGoals.current, normalizeGoals(customGoals));
    const requiresMonthlySavings = selectedGoalRequiresMonthlySavings(customGoals);

    // If user came from financial dashboard, save goals and navigate back to dashboard
    if (isFromFinancialDashboard && application?.id) {
      if (requiresMonthlySavings) {
        navigate(RoutePath.AICoachMonthlySavings, {
          state: { isFromFinancialDashboard: true, goalsChanged } as AICoachGoalsFlowState,
        });
        return;
      }

      if (goalsChanged) {
        await dispatchWithUnwrap(
          updateAICoachGoals({ applicationId: application.id, goals: customGoals, mode: UpdateGoalsMode.Replace }),
        );
        dispatch(resetGoalOptimizerState());
        dispatch(resetAiCoachConversation());
        dispatch(toggleAIChat());
      }
      navigate(RoutePath.FinancialDashboard);
      return;
    }

    if (requiresMonthlySavings) {
      navigate(RoutePath.AICoachMonthlySavings);
      return;
    }

    handleNext();
  };

  const onChange = (fieldName: FormFields, value: string) => {
    setValue(fieldName, value);
    trigger(fieldName);
  };

  const canSubmit = effectiveGoals && effectiveGoals?.length > 0 && isValid;

  return (
    <div>
      <FormContainer title="What are your goals?">
        <div className={styles.mainContainer}>
          <div className={styles.goalsContainer}>
            {aiCoachGoalsList.map((option) => (
              <ToggleButton
                key={option.value}
                type={ToggleButtonType.Radio}
                onClick={handleGoalChange}
                selected={effectiveGoals?.some((item) => item.type === option.value)}
                {...option}
              />
            ))}
          </div>

          {isSaveForExpenseSelected && (
            <>
              <Input
                label="What expense do you want to save for?"
                errorMessage={errors[FormFields.ExpenseGoal]?.message}
                name={FormFields.ExpenseGoal}
                onChange={(e) => onChange(FormFields.ExpenseGoal, e.target.value)}
                value={watcher[FormFields.ExpenseGoal]}
                onKeyUp={(e) => e.key === 'Enter' && isValid && handleContinue()}
              />
              <NumberInput
                label="Set your expense amount:"
                prefix="$"
                placeholder="$"
                errorMessage={errors[FormFields.ExpenseAmount]?.message}
                thousandSeparator
                name={FormFields.ExpenseAmount}
                onChange={(event) => {
                  const { value } = event.target;
                  setValue(FormFields.ExpenseAmount, getParsedNumericValue(value));
                  trigger(FormFields.ExpenseAmount);
                }}
                value={watcher[FormFields.ExpenseAmount] ? `${watcher[FormFields.ExpenseAmount]}` : undefined}
                inputMode="numeric"
                onKeyUp={(e) => e.key === 'Enter' && isValid && handleContinue()}
              />
            </>
          )}

          {isOtherSelected && (
            <>
              <Input
                label="What other goal do you have?"
                errorMessage={errors[FormFields.OtherGoal]?.message}
                name={FormFields.OtherGoal}
                onChange={(e) => onChange(FormFields.OtherGoal, e.target.value)}
                value={watcher[FormFields.OtherGoal]}
                onKeyUp={(e) => e.key === 'Enter' && isValid && handleContinue()}
              />
              <NumberInput
                label="Set your goal amount:"
                prefix="$"
                placeholder="$"
                errorMessage={errors[FormFields.OtherGoalAmount]?.message}
                thousandSeparator
                name={FormFields.OtherGoalAmount}
                onChange={(event) => {
                  const { value } = event.target;
                  setValue(FormFields.OtherGoalAmount, getParsedNumericValue(value));
                  trigger(FormFields.OtherGoalAmount);
                }}
                value={watcher[FormFields.OtherGoalAmount] ? `${watcher[FormFields.OtherGoalAmount]}` : undefined}
                inputMode="numeric"
                onKeyUp={(e) => e.key === 'Enter' && isValid && handleContinue()}
              />
            </>
          )}

          <Button onClick={handleContinue} disabled={!canSubmit} isLoading={isLoading && !!application?.id}>
            Next
          </Button>
        </div>
      </FormContainer>
    </div>
  );
};

export default AICoachGoals;
